import type { BoxProps, StackProps } from '@chakra-ui/react';
import { Box, HStack, Image } from '@chakra-ui/react';
import type { Fraction } from 'bi-fraction';
import { useMemo } from 'react';

import { Percent } from './Num';

export type GraduationBarProps = StackProps & {
  percent: Fraction;
  nOfHeart?: number;
};

export const GraduationBar = ({
  percent,
  nOfHeart = 5,
  ...props
}: GraduationBarProps) => {
  const isGraduated = percent.eq(1);

  const variants = useMemo((): GraduationHeartProps['variant'][] => {
    if (isGraduated) {
      return [...Array(nOfHeart)].map(() => 'red');
    }

    const percentOfHeart = percent.mul(nOfHeart);
    const intPart = Number(percentOfHeart.quotient);

    return [...Array(nOfHeart)].map((_, i): GraduationHeartProps['variant'] => {
      if (i + 1 <= intPart) {
        return 'green';
      }
      if (i === intPart && percentOfHeart.remainder.gt(0)) {
        return 'half';
      }
      return 'empty';
    });
  }, [isGraduated, nOfHeart, percent]);

  return (
    <HStack gap="2px" {...props}>
      {[...Array(nOfHeart)].map((_, i) => {
        return <GraduationHeart key={i} variant={variants[i]} />;
      })}
      <Percent
        value={percent}
        textStyle="body1Bold"
        color={isGraduated ? 'primary' : 'state.success'}
        ml="6px"
      />
    </HStack>
  );
};

type GraduationHeartProps = BoxProps & {
  variant: 'red' | 'green' | 'half' | 'empty';
};
const GraduationHeart = ({ variant, ...props }: GraduationHeartProps) => {
  return (
    <Box boxSize="12px" position="relative" {...props}>
      {(variant === 'half' || variant === 'empty') && (
        <Image
          boxSize="100%"
          src="/images/graduation-bar/empty.svg"
          alt="slot"
        />
      )}
      {variant !== 'empty' && (
        <Image
          w={variant === 'half' ? '50%' : '100%'}
          h="100%"
          src={
            variant === 'red'
              ? '/images/graduation-bar/red.svg'
              : '/images/graduation-bar/green.svg'
          }
          objectPosition="left top"
          position="absolute"
          left="0"
          top="0"
          alt="heart"
        />
      )}
    </Box>
  );
};
